import {
  useMemo,
  createContext,
  useContext,
  useState,
  useCallback,
} from 'react'
import { DefaultGenerics, Channel as SCChannel, StreamChat } from 'stream-chat'
import { useAuth } from '@contexts/Auth'
import {
  streamCreateChatChannel,
  streamCreateChatClient,
} from '@hooks/useStream'
import { v4 as uuidv4 } from 'uuid'

type ContextType = {
  client?: StreamChat<DefaultGenerics>
  channel?: SCChannel
  resetMessageHistory: () => void
  getHistory: () => unknown[]
  setHistory: (history: unknown[]) => void
  pending: boolean
  setPending: (state: boolean) => void
}

const Context = createContext<ContextType>({} as ContextType)

type ChatSuportProviderProps = {
  children: React.ReactNode
}

function ChatSuportProvider({ children }: ChatSuportProviderProps) {
  const { user } = useAuth()
  const [client, setClient] = useState<StreamChat<DefaultGenerics>>()
  const [channel, setChannel] = useState<SCChannel>()
  const [pending, setPending] = useState<boolean>(false)
  const [tabId] = useState<string>(uuidv4())

  const getHistory = useCallback(() => {
    return JSON.parse(
      sessionStorage.getItem(`@NOMOS:chatHistory${tabId}`) || '[]'
    )
  }, [])

  const setHistory = useCallback((history: unknown[]) => {
    sessionStorage.setItem(
      `@NOMOS:chatHistory${tabId}`,
      JSON.stringify(history)
    )
  }, [])

  const resetMessageHistory = useCallback(() => {
    setHistory([])
  }, [])

  useMemo(() => {
    if (!client && user && user.id && user.name) {
      resetMessageHistory()
      streamCreateChatClient(
        process.env.REACT_APP_STREAM_KEY!,
        process.env.REACT_APP_STREAM_SECRET!,
        user.id,
        user.name
      ).then((c) => {
        setClient(c)
        streamCreateChatChannel(user.id!, `${user.id}ChannelId`, c).then(
          (localChannel) => {
            setChannel(localChannel)
          }
        )
      })
    }
  }, [user?.id, user?.name])

  const chatSuportProviderValues = useMemo(
    () => ({
      client,
      channel,
      resetMessageHistory,
      pending,
      getHistory,
      setHistory,
      setPending,
    }),
    [
      client,
      channel,
      resetMessageHistory,
      pending,
      getHistory,
      setHistory,
      setPending,
    ]
  )

  return (
    <Context.Provider value={chatSuportProviderValues}>
      {children}
    </Context.Provider>
  )
}

const useChatSuport = () => {
  const context = useContext(Context)
  return context
}

export { ChatSuportProvider, useChatSuport }
